import React, { useState } from 'react';
import type { Control, FieldError, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { useController } from 'react-hook-form';
import styled from 'styled-components';
import type { PrivacyFeedbackFormData } from './types';
import { ErrorMessage } from './sharedStyledComponents';
import { CheckBoxLabels, CheckboxOptions } from './constants';

type PrivacyCheckboxesProps = {
    options: typeof CheckboxOptions;
    name: 'checkboxes';
    control: Control<PrivacyFeedbackFormData, any>;
    error?: FieldError;
    setError: UseFormSetError<PrivacyFeedbackFormData>;
    clearErrors: UseFormClearErrors<PrivacyFeedbackFormData>;
}

export const PrivacyFeedbackCheckboxes = ({ options, control, name, error, setError, clearErrors }: PrivacyCheckboxesProps) => {
    const { field } = useController({
        control,
        name
    });

    const [value, setValue] = useState<typeof CheckboxOptions[number][]>(field.value || []);

    const handleChange = (checked: boolean, optionValue: typeof CheckboxOptions[number]) => {
        const valueCopy = [...value];

        if (checked) {
            valueCopy.push(optionValue);
        } else {
            valueCopy.splice(valueCopy.indexOf(optionValue), 1);
        }

        field.onChange(valueCopy);

        // update local state
        setValue(valueCopy);
    };
  
    return (
        <CheckboxWrapper>
            <CheckboxInstructions>Please select one of the options below in order to process your request:</CheckboxInstructions>
            { options.map((option, index) => 
            {
                return (
                    <CheckboxRow key={option}>
                        <StyledCheckbox
                            onChange={(e) => handleChange(e.target.checked, e.target.value as typeof CheckboxOptions[number])}
                            id={option}
                            type='checkbox'
                            checked={value.includes(option)}
                            value={option}
                        />
                        <CheckboxLabel htmlFor={option}>{CheckBoxLabels[option]}</CheckboxLabel>
                    </CheckboxRow>
                );
            })}
            {error?.message && <ErrorMessage role='alert'>{error?.message}</ErrorMessage>}
        </CheckboxWrapper>
    );
};

const CheckboxWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 9px;
    margin: 9px 0 9px 0;
`;

const CheckboxInstructions = styled.span`
    font-size: 15px;
    margin-bottom: 5px;
    font-weight: bold;
`;

const CheckboxRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 7px;
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
`;

const StyledCheckbox = styled.input`
    width: 18px;
    height: 18px;
    cursor: pointer;
    &:checked {
        background-color: #007bff;
        border-radius: #007bff;
    }
`;
